import { FC, useEffect, useState } from 'react';
import { Modal } from 'vopay';
import { ELinxConnect } from '#shared/components/ELinx';
import AddInteracModal, { type FormValues as AddInteracFormValues } from '../AddInteracModal';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import usePaymentMethods, { type PaymentMethodType } from '#shared/components/Contacts/hooks/usePaymentMethods';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import Options, { type OptionPaymentMethod } from './Options';

type ProvidePaymentMethodProps = {
  isOpen: boolean;
  accountID: string;
  accountName: string;
  omit?: OptionPaymentMethod[];
  onClose: () => void;
  onAddInterac: (token: string, eTransferMethod: AddInteracFormValues['eTransferMethod']) => void;
  onBankAdded: (token: string) => void;
  onCreditCardAdded: (token: string) => void;
  onDebitCardAdded: (token: string) => void;
  onDigitalWalletAdded: (type: 'PayPal' | 'Venmo' | 'Zelle', token: string) => void;
  // Host facts the portal read from its ClientData/LockedFeatures providers.
  clientAccountsEnabled: boolean;
  country: string;
  loginUserName?: string;
  lockedSelectOptions?: { [featureKey: string]: string };
  onLockedFeature?: (featureName: string) => void;
};

type ELinxConnectMethod = 'bank' | 'credit' | 'debitcard' | 'paypal' | 'venmo' | 'zelle';

const eLinxConnectMethodToPaymentMethod: Record<
  Extract<ELinxConnectMethod, 'paypal' | 'venmo' | 'zelle'>,
  Extract<PaymentMethodType, 'PayPal' | 'Venmo' | 'Zelle'>
> = {
  paypal: 'PayPal',
  venmo: 'Venmo',
  zelle: 'Zelle',
};

const ProvidePaymentMethod: FC<ProvidePaymentMethodProps> = ({
  isOpen: showingPaymentMethod,
  onClose,
  accountID,
  accountName,
  omit = [],
  onAddInterac,
  onBankAdded,
  onCreditCardAdded,
  onDebitCardAdded,
  onDigitalWalletAdded,
  clientAccountsEnabled,
  country,
  loginUserName,
  lockedSelectOptions,
  onLockedFeature,
}) => {
  const [methodType, setMethodType] = useState<ELinxConnectMethod | null>(null);
  const [showingInteracForm, setShowingInteracForm] = useState(false);
  const [isProvidingDetails, setIsProvidingDetails] = useState(false);
  const alertDialog = useAlertDialog();

  const { t } = useTranslation('translations', {
    keyPrefix: 'Shared.Customers.PaymentMethodsTab.ProvidePaymentMethod',
  });

  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Shared' });

  const { addInterac } = usePaymentMethods({
    accountID: accountID,
    clientAccountsEnabled,
    loginUserName,
  });

  const handleProvideDetails = (method: ELinxConnectMethod) => {
    setMethodType(method);
    setIsProvidingDetails(true);
  };

  useEffect(() => {
    if (!showingPaymentMethod) setShowingInteracForm(false);
  }, [showingPaymentMethod]);

  function handleSelect(type: OptionPaymentMethod) {
    if (type === 'Email') {
      setShowingInteracForm(true);
    } else {
      const map: Record<Exclude<OptionPaymentMethod, 'Email'>, ELinxConnectMethod> = {
        BankAccount: 'bank',
        CreditCard: 'credit',
        DebitCard: 'debitcard',
        PayPal: 'paypal',
        Venmo: 'venmo',
        Zelle: 'zelle',
      };

      handleProvideDetails(map[type]);
    }
  }

  const handleAddInterac = async (params: AddInteracFormValues) => {
    const addResponse = await addInterac({
      ...params,
      recipientName: accountName,
    });

    if (!addResponse.Success) {
      await alertDialog.error(
        t('Dialog.AddMethod.error', { defaultValue: 'Something went wrong trying to add your payment method.' }),
        null,
        {
          abortLabel: tGlobal('Buttons.back', { defaultValue: 'Back' }),
        },
      );

      return;
    }

    onAddInterac(addResponse.Token, params.eTransferMethod);
  };

  const content = (
    <>
      <Modal isVisible={showingPaymentMethod} enableBgClick={false}>
        <Options
          omit={omit}
          onClose={onClose}
          onSelect={handleSelect}
          country={country}
          lockedSelectOptions={lockedSelectOptions}
          onLockedFeature={onLockedFeature}
        />
      </Modal>

      <AddInteracModal
        isOpen={showingInteracForm}
        onClose={() => setShowingInteracForm(false)}
        onSubmit={handleAddInterac}
      />

      {isProvidingDetails && (
        <ELinxConnect
          setting={{
            RedirectURL: 'https://vopay.com',
            Country: country,
            PaymentSelectionMethod: methodType as string,
            ClientControlled: true,
            RedirectMethod: 'JavascriptMessage',
            ClientAccountID: accountID,
          }}
          onClose={() => setIsProvidingDetails(false)}
          onSuccess={(event) => {
            onClose();
            setIsProvidingDetails(false);

            if (methodType === 'bank') {
              onBankAdded(event.data.Token);
            }

            if (methodType === 'credit') {
              onCreditCardAdded(event.data.Token);
            }

            if (methodType === 'debitcard') {
              onDebitCardAdded(event.data.Token);
            }

            if (methodType === 'paypal' || methodType === 'venmo' || methodType === 'zelle') {
              onDigitalWalletAdded(eLinxConnectMethodToPaymentMethod[methodType], event.data.Token);
            }
          }}
        />
      )}
    </>
  );

  // The portal mounted this into its app-level #modal-root; shared can't assume
  // the host provides one, so fall back to document.body (same as shared's
  // ELinxConnect provider).
  return createPortal(content, document.getElementById('modal-root') ?? document.body);
};

export default ProvidePaymentMethod;
